import React from "react";
import { Text, TouchableOpacity, View } from "react-native";

export type TimePeriod = "AM" | "PM";

export type TimeSpinnerProps = {
  hour: number;
  minute: number;
  period: TimePeriod;
  onHourChange: (h: number) => void;
  onMinuteChange: (m: number) => void;
  onPeriodChange: (p: TimePeriod) => void;
  label: string;
};

export const MINUTES = [0, 15, 30, 45];

const pad = (n: number) => String(n).padStart(2, "0");

const TimeSpinner = ({
  hour,
  minute,
  period,
  onHourChange,
  onMinuteChange,
  onPeriodChange,
  label,
}: TimeSpinnerProps) => (
  <View className="items-center gap-1">
    <Text className="text-xs text-gray-dark mb-1">{label}</Text>

    <View className="flex-row items-center gap-1">
      {/* ── Hour column ── */}
      <View className="items-center">
        <TouchableOpacity
          onPress={() => onHourChange(hour === 1 ? 12 : hour - 1)}
          className="px-2 py-0.5 hover:bg-ash-3 hover:rounded-full"
        >
          <Text className="text-gray-dark text-xs">▲</Text>
        </TouchableOpacity>

        <View className="bg-gray-light rounded-lg px-3 py-1.5 min-w-[40px] items-center">
          <Text className="text-base font-bold text-gray-dark-2">
            {pad(hour)}
          </Text>
        </View>

        <TouchableOpacity
          onPress={() => onHourChange(hour === 12 ? 1 : hour + 1)}
          className="px-2 py-0.5 hover:bg-ash-3 hover:rounded-full"
        >
          <Text className="text-gray-dark text-xs">▼</Text>
        </TouchableOpacity>
      </View>

      <Text className="text-base font-bold text-gray-dark-2 mb-0.5">:</Text>

      {/* ── Minute column ── */}
      <View className="items-center">
        <TouchableOpacity
          onPress={() => {
            const idx = MINUTES.indexOf(minute);
            onMinuteChange(
              MINUTES[(idx - 1 + MINUTES.length) % MINUTES.length],
            );
          }}
          className="px-2 py-0.5 hover:bg-ash-3 hover:rounded-full"
        >
          <Text className="text-gray-dark text-xs">▲</Text>
        </TouchableOpacity>

        <View className="bg-gray-light rounded-lg px-3 py-1.5 min-w-[40px] items-center">
          <Text className="text-base font-bold text-gray-dark-2">
            {pad(minute)}
          </Text>
        </View>

        <TouchableOpacity
          onPress={() => {
            const idx = MINUTES.indexOf(minute);
            onMinuteChange(MINUTES[(idx + 1) % MINUTES.length]);
          }}
          className="px-2 py-0.5 hover:bg-ash-3 hover:rounded-full"
        >
          <Text className="text-gray-dark text-xs">▼</Text>
        </TouchableOpacity>
      </View>

      {/* ── AM / PM toggle ── */}
      <View className="ml-1 rounded-lg overflow-hidden border border-ash-3">
        {(["AM", "PM"] as const).map((p) => (
          <TouchableOpacity
            key={p}
            onPress={() => onPeriodChange(p)}
            className={`px-2 py-1 ${period === p ? "bg-primary hover:opacity-90" : "bg-chalk hover:bg-ash-3"}`}
          >
            <Text
              className={`text-xs font-semibold ${
                period === p ? "text-white" : "text-gray-dark"
              }`}
            >
              {p}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  </View>
);

export default TimeSpinner;
